import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { BiPlus } from 'react-icons/bi'
import { HiOutlineUserGroup } from 'react-icons/hi'
import { getSocket } from '../store/socketClient'
import * as authAction from '../store/authAction'
import { useAuthGate } from '../comps/authPrompt'
import { createRoom, fetchRooms, ROOM_BACKGROUNDS, ROOM_LIMITS } from '../data/rooms_data'
import { createCommunity } from '../data/communities_data'
import CommunityJoinRules from '../comps/communityJoinRules'
import { uploadPostFile } from '../utils/postImages'
import '../styles/rooms.css'
import '../styles/community.css'

const Rooms = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const user = useSelector(state => state.authReduse)
  const { requireAuth, promptEl } = useAuthGate()
  const [rooms, setRooms] = useState([])
  const [open, setOpen] = useState(false)
  const [communityOpen, setCommunityOpen] = useState(false)
  const [communityName, setCommunityName] = useState('')
  const [communityBio, setCommunityBio] = useState('')
  const [communityFile, setCommunityFile] = useState(null)
  const [communityRule, setCommunityRule] = useState('admin')
  const [title, setTitle] = useState('')
  const [background, setBackground] = useState('night')
  const [limit, setLimit] = useState(10)
  const [askToJoin, setAskToJoin] = useState(false)
  const [error, setError] = useState('')
  const [warning, setWarning] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const userData = localStorage.getItem('userdata')
    if (!userData) return undefined
    try {
      const { id, pid } = JSON.parse(userData)
      dispatch(authAction.auth(id, pid))
    } catch (err) {}
    return undefined
  }, [dispatch])

  useEffect(() => {
    let alive = true
    const load = async () => {
      try {
        const list = await fetchRooms()
        if (alive) setRooms(list)
      } catch (err) {
        if (alive) setError(err.message)
      }
    }
    load()
    const socket = getSocket()
    const onChange = (list) => { if (alive && Array.isArray(list)) setRooms(list) }
    socket.on('rooms:changed', onChange)
    const timer = window.setInterval(load, 12000)
    return () => {
      alive = false
      socket.off('rooms:changed', onChange)
      window.clearInterval(timer)
    }
  }, [])

  useEffect(() => {
    document.body.classList.toggle('roomsModalOpen', open || communityOpen || Boolean(warning))
    return () => document.body.classList.remove('roomsModalOpen')
  }, [open, communityOpen, warning])

  useEffect(() => {
    if (location.state && location.state.roomFull) {
      setWarning(location.state.roomFull)
      navigate('.', { replace: true, state: {} })
    }
  }, [location.state, navigate])

  const onCreateCommunity = async (event) => {
    event.preventDefault()
    if (!requireAuth()) return
    setSaving(true)
    setError('')
    try {
      let img = ''
      if (communityFile) img = await uploadPostFile(communityFile, 0)
      const community = await createCommunity({ name: communityName, bio: communityBio, img, joinRule: communityRule })
      navigate(`/community/${community.id}`)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const onCreate = async (event) => {
    event.preventDefault()
    if (!requireAuth()) return
    setSaving(true)
    setError('')
    try {
      const room = await createRoom({
        userid: user.id,
        name: user.name || user.email,
        photo: user.photo,
        title,
        background,
        limit,
        askToJoin
      })
      navigate(`/rooms/${room.id}`)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const joinRoom = (room) => {
    if (!requireAuth()) return
    if (room.count >= room.limit) {
      setWarning(`This room is full (${room.count}/${room.limit}). Wait for someone to leave or join another room.`)
      return
    }
    navigate(`/rooms/${room.id}`)
  }

  return (
    <section className="RoomsPage">
      {promptEl}
      <Helmet>
        <title>Rooms</title>
        <meta name="theme-color" content="#000000" />
      </Helmet>
      <header className="roomsHead">
        <div>
          <p className="roomsEyebrow">Live</p>
          <h1>Rooms</h1>
        </div>
        <div className="roomsHeadActions">
          <button type="button" className="roomsCreateBtn zombieRoomLink" onClick={() => navigate('/zombie')}>
            ☠ Zombie
          </button>
          <button type="button" className="roomsCreateBtn roomsCreateGhost" onClick={() => navigate('/community')}>
            Community
          </button>
          <button type="button" className="roomsCreateBtn roomsCreateGhost" onClick={() => { if (requireAuth()) setCommunityOpen(true) }}>
            <BiPlus /> Create community
          </button>
          <button type="button" className="roomsCreateBtn" onClick={() => { if (requireAuth()) setOpen(true) }}>
            <BiPlus /> Create
          </button>
        </div>
      </header>
      {error && <p className="roomsError">{error}</p>}
      {warning && (
        <div className="roomsWarn" role="alertdialog" aria-modal="true">
          <div className="roomsWarnCard">
            <h2>{/full/i.test(warning) ? 'Room is full' : 'Cannot enter'}</h2>
            <p>{warning}</p>
            <button type="button" onClick={() => setWarning('')}>OK</button>
          </div>
        </div>
      )}
      <div className="roomsList">
        {rooms.length === 0 && (
          <div className="roomsEmpty">
            <HiOutlineUserGroup />
            <p>No rooms yet. Create one and people can join.</p>
            <button type="button" className="roomsCreateBtn" onClick={() => { if (requireAuth()) setOpen(true) }}>
              <BiPlus /> Create a room
            </button>
          </div>
        )}
        {rooms.map(room => (
          <button
            type="button"
            key={room.id}
            className={`roomsCard roomsBg--${room.background}`}
            onClick={() => joinRoom(room)}
          >
            <span className="roomsCardName">{room.name}</span>
            <span className="roomsCardMeta">
              {room.count}/{room.limit} · host @{room.hostName}
              {room.askToJoin ? ' · ask to join' : ''}
            </span>
            <span className="roomsCardJoin">{room.count >= room.limit ? 'Full' : room.askToJoin ? 'Ask to join' : 'Join'}</span>
          </button>
        ))}
      </div>

      {open && (
        <div className="roomsModal" onClick={() => setOpen(false)} role="presentation">
          <form className="roomsModalCard" onClick={event => event.stopPropagation()} onSubmit={onCreate}>
            <h2>Create a room</h2>
            <label>
              Name
              <input value={title} onChange={event => setTitle(event.target.value)} placeholder="Friday hangout" maxLength={40} required />
            </label>
            <p className="roomsFieldLabel">Background</p>
            <div className="roomsBgPick">
              {ROOM_BACKGROUNDS.map(item => (
                <button
                  type="button"
                  key={item.id}
                  className={`roomsBgSwatch roomsBg--${item.id}${background === item.id ? ' isOn' : ''}`}
                  onClick={() => setBackground(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <p className="roomsFieldLabel">People who can enter</p>
            <div className="roomsLimitPick">
              {ROOM_LIMITS.map(value => (
                <button
                  type="button"
                  key={value}
                  className={`roomsLimit${limit === value ? ' isOn' : ''}`}
                  onClick={() => setLimit(value)}
                >
                  {value}
                </button>
              ))}
            </div>
            <div className="roomsSeatPreview">
              {Array.from({ length: Math.min(limit, 10) }).map((_, index) => (
                <span key={index} className="roomsSeat" aria-hidden="true"><BiPlus /></span>
              ))}
              {limit > 10 && <span className="roomsSeat roomsSeat--more">+{limit - 10}</span>}
            </div>
            <p className="roomsFieldLabel">Who can enter</p>
            <div className="roomsAccessPick">
              <button type="button" className={`roomsAccess${askToJoin ? '' : ' isOn'}`} onClick={() => setAskToJoin(false)}>
                Anyone
              </button>
              <button type="button" className={`roomsAccess${askToJoin ? ' isOn' : ''}`} onClick={() => setAskToJoin(true)}>
                Ask permission
              </button>
            </div>
            <div className="roomsModalActions">
              <button type="button" onClick={() => setOpen(false)}>Cancel</button>
              <button type="submit" disabled={saving}>{saving ? 'Creating…' : 'Create room'}</button>
            </div>
          </form>
        </div>
      )}

      {communityOpen && (
        <div className="roomsModal" onClick={() => setCommunityOpen(false)} role="presentation">
          <form className="roomsModalCard" onClick={event => event.stopPropagation()} onSubmit={onCreateCommunity}>
            <h2>Create community</h2>
            <label>
              Name
              <input value={communityName} onChange={event => setCommunityName(event.target.value)} placeholder="Night photographers" maxLength={40} required minLength={3} />
            </label>
            <label>
              Bio
              <input value={communityBio} onChange={event => setCommunityBio(event.target.value)} placeholder="What is this group about?" maxLength={400} />
            </label>
            <label>
              Photo
              <input type="file" accept="image/*" onChange={event => setCommunityFile(event.target.files && event.target.files[0])} />
            </label>
            <CommunityJoinRules value={communityRule} onChange={setCommunityRule} />
            <div className="roomsModalActions">
              <button type="button" onClick={() => setCommunityOpen(false)}>Cancel</button>
              <button type="submit" disabled={saving}>{saving ? 'Creating…' : 'Create community'}</button>
            </div>
          </form>
        </div>
      )}
    </section>
  )
}

export default Rooms
